"use client";

import { StaticRouteMapCard } from "@/components/movement/static-route-map-card";
import type { MovementActivityPostPayload } from "@/lib/movement/activity-post-payload";
import {
  formatDistance,
  formatDuration,
  formatPace,
} from "@/lib/movement/gps-utils";

type MovementActivityDisplayPreviewProps = {
  activityId: string;
  label: string;
  payload: MovementActivityPostPayload;
};

export function MovementActivityDisplayPreview({
  activityId,
  label,
  payload,
}: MovementActivityDisplayPreviewProps) {
  const distanceLabel = formatDistance(payload.distanceMeters, true);
  const durationLabel = formatDuration(payload.durationSeconds);
  const elevationLabel = `${Math.round(payload.elevationGainMeters ?? 0)}m`;

  const averageSpeedKmh =
    payload.durationSeconds > 0
      ? (payload.distanceMeters / 1000 / payload.durationSeconds) * 3600
      : 0;

  const paceOrSpeedLabel =
    payload.movementType === "ride"
      ? `${averageSpeedKmh.toFixed(1)} km/h`
      : payload.avgPaceSecondsPerKm != null && payload.avgPaceSecondsPerKm > 0
        ? `${formatPace(payload.avgPaceSecondsPerKm)} /km`
        : "—";

  const stats = [
    { key: "distance", label: "Distance", value: distanceLabel },
    { key: "time", label: "Time", value: durationLabel },
    {
      key: "pace",
      label: payload.movementType === "ride" ? "Avg speed" : "Avg pace",
      value: paceOrSpeedLabel,
    },
    { key: "elevation", label: "Elev gain", value: elevationLabel },
  ];

  // No card chrome of its own: the feed/quote wrapper already rounds and
  // clips this block, so nesting another border reads as a double frame.
  return (
    <div className="bg-surface">
      <div className="relative h-48 overflow-hidden">
        <StaticRouteMapCard
          mapId={`feed-mv-${activityId}-${payload.movementActivityId}`}
          coordinates={payload.previewPoints}
          segments={payload.previewSegments}
          mode="tile"
          showAttribution={false}
          emptyStateLabel="No route points for this activity"
        />
        <div className="absolute right-3 bottom-2.5 left-3">
          <div className="inline-flex items-center rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white/95 backdrop-blur">
            {label}
          </div>
        </div>
      </div>

      {/* Strava-style stat strip: solid, compact, no floating boxes. */}
      <div className="grid grid-cols-4 divide-x divide-border border-t border-border">
        {stats.map((stat) => (
          <div key={stat.key} className="min-w-0 px-2 py-2 text-center">
            <div className="truncate text-[10px] tracking-wide text-text-tertiary uppercase">
              {stat.label}
            </div>
            <div className="mt-0.5 truncate text-[13px] font-semibold text-text-primary tabular-nums">
              {stat.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
